import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import { selectAdminApiUrl } from 'util/selector/apiSelector';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import MdEventNote from '@mui/icons-material/EventNote';
import FileDownload from '@mui/icons-material/FileUpload';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import { Box, Divider, Grid } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { AdminRoute } from 'app/route/admin';
import { RaisedButton, SecondaryButton } from 'component/Button/Button';
import { BaseTextField } from 'component/Form/BaseTextField';
import { Pagination } from 'component/Pagination/Pagination';
import { ResourceLoader } from 'component/ResourceLoaderNew/ResourceLoader';
import { FeatureFlags } from 'config/featureFlags';
import qs from 'qs';
import { useAppDispatch } from 'reduxStore/appStore';
import { selectIsAdmin, selectIsWBAdmin } from 'reduxStore/auth/selectors';
import { selectCircles } from 'reduxStore/circles/selectors';
import { fetchClients } from 'reduxStore/client/asyncActions';
import { selectClientListResource, selectClientPagination } from 'reduxStore/client/selectors';
import { clearClientState } from 'reduxStore/client/slice';
import { openModal } from 'reduxStore/modal/slice';
import { AdminModal } from 'register/AdminModal';
import { openInNewTab } from 'router/routerAction';
import { usePaginationPage } from 'shared/hooks/usePaginationPage';
import { useSearchFilters } from 'shared/hooks/useSearchFilters';
import { ClientsTable } from 'view/ClientsPage/ClientsTable/ClientsTable';
import { CreateClientModal } from 'view/ClientsPage/CreateClient/CreateClientModal';
import { ClientListFilter, clientListFilterEmptyValue } from 'view/ClientsPage/model';
import { projectListFilterEmptyValue } from 'view/ProjectsListPage/model';
import { MenuTitle } from 'view/ProjectsListPage/ProjectsListPage.s';

export const ClientsPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const circles = useSelector(selectCircles);
  const hasSingleCircle = circles.length === 1;
  const singleCircleId = circles[0]?.id;

  const isAdmin = useSelector(selectIsAdmin);
  const isWBAdmin = useSelector(selectIsWBAdmin);
  const isTemplateLibraryUploadEnabled = useFeatureIsOn(FeatureFlags.TEMPLATE_LIBRARY_UPLOAD);
  const clientsResource = useSelector(selectClientListResource);
  const [exportSurveyDatesUrl, setExportSurveyDatesUrl] = useState<string>();

  const clientPagination = useSelector(selectClientPagination);
  const totalPages = clientPagination?.pages;
  const totalNumber = clientPagination?.total;

  const { page, limit, setPaginationPage, resetPaginationPage } = usePaginationPage({
    initialValue: { page: 1, limit: 10 },
    paginationResult: {
      pages: totalPages,
      total: totalNumber,
    },
  });
  const { filters, debouncedFilters, mergeFilters } = useSearchFilters<ClientListFilter>(
    { searchText: '', circleId: clientListFilterEmptyValue },
    { resetPaginationPage },
  );

  useEffect(() => {
    const circleIdFilterValue =
      filters.circleId === projectListFilterEmptyValue ? undefined : filters.circleId;
    const circleId = hasSingleCircle ? singleCircleId : circleIdFilterValue;

    setExportSurveyDatesUrl(
      `${selectAdminApiUrl()}/api/v1/company/survey-dates-report${qs.stringify(
        { filter: { circleId } },
        { encode: false, skipNulls: true, addQueryPrefix: true },
      )}`,
    );

    dispatch(fetchClients({ searchText: debouncedFilters.searchText, page, limit, circleId }));
  }, [
    debouncedFilters.searchText,
    dispatch,
    filters.circleId,
    hasSingleCircle,
    limit,
    page,
    singleCircleId,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearClientState());
    };
  }, [dispatch]);

  const onCreateClient = () => dispatch(openModal({ name: AdminModal.CreateClient, params: {} }));

  const onManageSurveyTemplates = () => dispatch(push(AdminRoute.PULSE_SURVEY_TEMPLATES_PATTERN));

  const onViewClick = (url: string) => {
    return dispatch(openInNewTab(url));
  };

  return (
    <>
      <Grid container columnSpacing={2} mb={3} alignItems="end" justifyContent="space-between">
        <Grid item xs={12} sm={3}>
          <RaisedButton
            id="createClientButton"
            label="Create Client"
            endIcon={<MdEventNote />}
            onClick={onCreateClient}
            fullWidth
            data-testid="createClientButton"
            color="primary"
          />
        </Grid>
        {isTemplateLibraryUploadEnabled && (
          <Grid item xs={12} sm={3}>
            <RaisedButton
              label="Manage Survey Templates"
              endIcon={<LibraryBooks />}
              data-testid="manageSurveyTemplates"
              onClick={onManageSurveyTemplates}
              fullWidth
              color="primary"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          {isAdmin && (
            <SecondaryButton
              label="Export Survey Dates"
              endIcon={<FileDownload />}
              data-testid="exportSurveyDates"
              href={exportSurveyDatesUrl}
              sx={{ mt: 2 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={3} mt={1}>
          <BaseTextField
            value={filters.searchText}
            onChange={(event) => mergeFilters({ searchText: event.target.value })}
            label="Search Clients..."
            data-testid="clientsSearchText"
            removeBottomMargin
            fullWidth
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid container columnSpacing={2} mt={2} alignItems="end" justifyContent="space-between">
        <Grid item xs={12} sm={3}>
          <MenuTitle>Clients</MenuTitle>
        </Grid>
        <Grid item xs={12} sm={6}></Grid>
        <Grid item xs={12} sm={3}>
          {isWBAdmin && circles.length > 1 && (
            <BaseTextField
              fullWidth
              data-testid="circleFilterSelect"
              label="Filter by circle"
              value={filters.circleId}
              onChange={(event) => {
                mergeFilters({ circleId: event.target.value });
              }}
              select
            >
              <MenuItem value={projectListFilterEmptyValue}>Show all</MenuItem>
              {circles.map((circle, index) => (
                <MenuItem key={index} value={circle.id}>
                  {circle.name}
                </MenuItem>
              ))}
            </BaseTextField>
          )}
        </Grid>
      </Grid>

      <ResourceLoader resource={clientsResource}>
        {(clients) => {
          return (
            <Grid
              container
              columnSpacing={2}
              mb={3}
              alignItems="end"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm={9}>
                <ClientsTable clients={clients} onViewClick={onViewClick} />
                {totalPages > 1 && (
                  <Box mt={4} data-testid="clientsPagination">
                    <Pagination
                      page={page - 1}
                      pageCount={totalPages}
                      onPageChange={(newPage) => setPaginationPage(newPage + 1)}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          );
        }}
      </ResourceLoader>
      <CreateClientModal />
    </>
  );
};
